/**
 * Exxy's chat body (docs/modules/assistant.md): the message list + composer, with no window chrome
 * so it can render inside the Messaging dock as a pinned conversation. Stateless endpoint — the
 * conversation lives in component state; the greeting is shown but never sent, so the first message
 * to the API is always the user's.
 */
import { isApiError } from '@oex/contracts';
import { Button, Input } from '@oex/ui';
import { Send } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useT } from '../../../app/i18n';
import { useAssistantChat, type ExxyTurn } from '../api';
import { ExxyText } from './rich-text';

export function ExxyChat() {
  const t = useT();
  const [input, setInput] = useState('');
  const [turns, setTurns] = useState<ExxyTurn[]>([]);
  const chat = useAssistantChat();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns, chat.isPending]);

  const send = async () => {
    const content = input.trim();
    if (!content || chat.isPending) return;
    const next = [...turns, { role: 'user' as const, content }];
    setTurns(next);
    setInput('');
    try {
      const res = await chat.mutateAsync({ data: { messages: next.slice(-30) } });
      setTurns((cur) => [...cur, { role: 'assistant', content: res.reply }]);
    } catch (e) {
      const detail = isApiError(e) ? (e.problem.detail ?? e.message) : t('assistant.error');
      setTurns((cur) => [...cur, { role: 'assistant', content: t('assistant.errorReply', { detail }) }]);
    }
  };

  return (
    <div className="flex h-full flex-col" data-testid="exxy-chat">
      <div ref={scrollRef} className="flex flex-1 flex-col gap-3 overflow-auto p-4">
        <Bubble role="assistant">{t('assistant.greeting')}</Bubble>
        {turns.map((m, i) => (
          <Bubble key={i} role={m.role}>
            {m.role === 'assistant' ? <ExxyText text={m.content} /> : m.content}
          </Bubble>
        ))}
        {chat.isPending ? (
          <Bubble role="assistant">
            <span className="inline-flex gap-1" aria-label={t('assistant.thinking')}>
              <Dot /> <Dot /> <Dot />
            </span>
          </Bubble>
        ) : null}
      </div>
      <form
        className="flex items-center gap-2 border-t border-border p-3"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={4000}
          placeholder={t('assistant.placeholder')}
          aria-label={t('assistant.inputLabel')}
        />
        <Button type="submit" size="sm" disabled={!input.trim()} loading={chat.isPending} aria-label={t('assistant.send')}>
          <Send aria-hidden className="size-4" />
        </Button>
      </form>
    </div>
  );
}

function Bubble({ role, children }: { role: 'user' | 'assistant'; children: ReactNode }) {
  const mine = role === 'user';
  return (
    <div className={mine ? 'self-end' : 'self-start'}>
      <div
        className={
          mine
            ? 'rounded-[4px] rounded-br-none bg-foreground px-3 py-2 text-sm text-background'
            : 'rounded-[4px] rounded-bl-none border border-border bg-background px-3 py-2 text-sm'
        }
      >
        <span className="whitespace-pre-wrap">{children}</span>
      </div>
    </div>
  );
}

function Dot() {
  return <span className="size-1.5 animate-pulse rounded-full bg-muted-foreground" />;
}
